import React, { useCallback, useEffect, useState } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { getTokenSilent, renewSessionInteractive, setSessionExpiredHandler, isInteractionRequiredError } from '../../utils/token';
import { SessionBanner, SessionBannerText, SessionBannerButton } from './sessionGuard.styled';

// Le refresh token SPA expire au bout de 24h : on sonde régulièrement pour détecter la coupure
// pendant que l'utilisateur ne fait rien, plutôt qu'au milieu d'une recherche.
const PROBE_INTERVAL_MS = 10 * 60 * 1000;

export default function SessionGuard() {
    const isAuthenticated = useIsAuthenticated();
    const [expired, setExpired] = useState(false);
    const [renewing, setRenewing] = useState(false);

    // Permet à getToken() de solliciter l'utilisateur au lieu de recharger la page
    useEffect(() => {
        setSessionExpiredHandler(() => setExpired(true));
        return () => setSessionExpiredHandler(null);
    }, []);

    const probe = useCallback(async () => {
        if (!isAuthenticated || document.visibilityState !== 'visible') return;
        try {
            await getTokenSilent();
            setExpired(false);
        } catch (error) {
            // Une erreur transitoire (initialisation MSAL, réseau) ne veut pas dire que la
            // session est morte : n'alerter que si une interaction est réellement requise.
            if (isInteractionRequiredError(error)) setExpired(true);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (!isAuthenticated) return;
        probe();
        const timer = setInterval(probe, PROBE_INTERVAL_MS);
        // Cas typique : onglet laissé ouvert la nuit, on vérifie dès le retour de l'utilisateur
        document.addEventListener('visibilitychange', probe);
        return () => {
            clearInterval(timer);
            document.removeEventListener('visibilitychange', probe);
        };
    }, [isAuthenticated, probe]);

    const handleRenew = async () => {
        setRenewing(true);
        try {
            await renewSessionInteractive();
            setExpired(false);
        } catch {
            // Popup fermée par l'utilisateur : on laisse le bandeau pour qu'il puisse réessayer
        } finally {
            setRenewing(false);
        }
    };

    if (!expired) return null;

    return (
        <SessionBanner role="alert">
            <i className="bx bx-time-five" />
            <SessionBannerText>
                Votre session Microsoft doit être renouvelée. Vos filtres et résultats sont conservés.
            </SessionBannerText>
            <SessionBannerButton type="button" onClick={handleRenew} disabled={renewing}>
                {renewing ? 'Renouvellement…' : 'Renouveler'}
            </SessionBannerButton>
        </SessionBanner>
    );
}
